import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BoardSelector } from './BoardSelector';
import { BoardToolbar } from './BoardToolbar';
import { KanbanBoard, Column, CardType } from './KanbanBoard';
import { auth } from '../../lib/firebase';
import {
  getAllBoards,
  saveBoard,
  deleteBoard,
  subscribeToBoards,
  Board as FirestoreBoard,
  debouncedSaveBoard,
  sendBoardInvite,
  removeBoardMembership
} from '../../lib/huntersBoardService';
import TagSidebar from './TagSidebar';
import { MethodologyPickerModal } from './MethodologyPickerModal';
import { Methodology, GuidedMethodology } from '../../lib/firebase';
import WorkBoardCanvas from './WorkBoardCanvas';
import CardModal from './CardModal';
import { Dialog } from '../ui/dialog';
import { db } from '../../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

// Types
interface BoardSummary {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  ownerId?: string;
}

interface Board {
  id: string;
  name: string;
  columns: Column[];
  cards: { [cardId: string]: CardType };
  createdAt: number;
  updatedAt: number;
  extra?: {
    cardPositions?: { [cardId: string]: { x: number; y: number } };
  };
  ownerId?: string;
  members?: string[];
  pendingInvites?: string[];
}

export interface HuntersBoardInviteHelpers {
  sendInvite: (email: string) => Promise<{ success: boolean; error?: string }>;
  getActiveBoardSummary: () => {
    boardId: string | null;
    boardName: string | null;
    pendingInvites: string[];
  };
}

interface HuntersBoardProps {
  boardsRefreshKey?: number;
  onInviteHelpersChange?: (helpers: HuntersBoardInviteHelpers | null) => void;
}

// Helpers
const defaultColumns = [
  { id: 'scope', title: '🎯 Scope & Intel', cardIds: [] },
  { id: 'todo', title: '🧠 To-Do / Methodology', cardIds: [] },
  { id: 'progress', title: '⚙️ In Progress', cardIds: [] },
  { id: 'findings', title: '✅ Findings / Vulnerabilities', cardIds: [] },
  { id: 'resources', title: '📚 Resources & Loot', cardIds: [] },
  { id: 'reporting', title: '📝 Reporting', cardIds: [] },
];

const createNewBoard = (name: string): Board => {
  const now = Date.now();
  return {
    id: `board-${now}`,
    name,
    columns: defaultColumns.map(col => ({ ...col, cardIds: [] })),
    cards: {},
    createdAt: now,
    updatedAt: now,
  };
};

// Helper to normalize column titles (remove emojis/special chars)
function normalizeTitle(title: string) {
  return title.replace(/[^ -\w\s/-]/gi, '').toLowerCase();
}

// Add to CardType interface (assume it's imported from KanbanBoard)
// lastEditedBy?: string; lastEditedAt?: number;

// Color palette for users
const USER_COLORS = [
  '#e57373', '#64b5f6', '#81c784', '#ffd54f', '#ba68c8', '#4db6ac', '#ff8a65', '#a1887f', '#90a4ae', '#f06292',
];

// Helper to ensure all boards have ownerId
function normalizeBoards(boards: Board[], userId: string): Board[] {
  return boards.map(b => ({ ...b, ownerId: b.ownerId || userId }));
}

const HuntersBoard: React.FC<HuntersBoardProps> = ({ boardsRefreshKey, onInviteHelpersChange }) => {
  // Board state
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [boardData, setBoardData] = useState<{ [id: string]: Board }>({});
  const [activeBoardId, setActiveBoardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // UI state
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showTagSidebar, setShowTagSidebar] = useState(true);
  const [groupByTag, setGroupByTag] = useState(false);
  const [showMethodologyPicker, setShowMethodologyPicker] = useState(false);
  const [attachedMethodology, setAttachedMethodology] = useState<Methodology | GuidedMethodology | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<string[]>([]);
  const [workBoardMode, setWorkBoardMode] = useState(false);
  const [stepState, setStepState] = useState<Record<string, { checked: boolean; note: string }>>({});

  // Real-time toggle for Firestore
  const [realtime, setRealtime] = useState(false);

  // Card modal state
  const [showCardModal, setShowCardModal] = useState(false);
  const [modalColId, setModalColId] = useState<string | null>(null);
  const [editCardId, setEditCardId] = useState<string | null>(null);

  // Card positions state for canvas mode
  const [canvasPositions, setCanvasPositions] = useState<{ [cardId: string]: { x: number; y: number } }>({});
  const syncTimer = useRef<NodeJS.Timeout | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInviteSentAt, setLastInviteSentAt] = useState<number>(0);

  // Local state for dropdown
  const [showInviteDropdown, setShowInviteDropdown] = useState(false);
  const inviteBtnRef = useRef<HTMLButtonElement>(null);

  // On mount, get user and load boards
  const prevBoardIdsRef = useRef<string[]>([]);
  useEffect(() => {
    let boardsUnsubscribe: (() => void) | null = null;
    
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        setLoading(true);
        
        // Clean up any existing subscription
        if (boardsUnsubscribe) {
          boardsUnsubscribe();
          boardsUnsubscribe = null;
        }
        
        const handleBoards = (boardsList: Board[]) => {
          const normalized = normalizeBoards(boardsList, user.uid);
          setBoards(normalized.map(b => ({ id: b.id, name: b.name, createdAt: b.createdAt, updatedAt: b.updatedAt, ownerId: b.ownerId || user.uid })));
          const data: { [id: string]: Board } = {};
          normalized.forEach(b => { data[b.id] = b; });
          setBoardData(data);
          // Detect new board
          const prevIds = prevBoardIdsRef.current;
          const newIds = normalized.map(b => b.id);
          const newBoardId = newIds.find(id => !prevIds.includes(id));
          if (newBoardId) {
            setActiveBoardId(newBoardId);
          } else if (!activeBoardId && normalized.length > 0) {
            setActiveBoardId(normalized[0].id);
          }
          prevBoardIdsRef.current = newIds;
          setLoading(false);
        };
        if (realtime) {
          // Real-time updates (higher quota usage)
          boardsUnsubscribe = subscribeToBoards(user.uid, handleBoards);
        } else {
          // One-time fetch (lower quota usage)
          const boards = await getAllBoards(user.uid);
          handleBoards(boards);
        }
      } else {
        setUserId(null);
        // Fallback to local state
        if (boards.length === 0) {
          const defaultBoard = createNewBoard('My First Board');
          setBoards([{ id: defaultBoard.id, name: defaultBoard.name, createdAt: defaultBoard.createdAt, updatedAt: defaultBoard.updatedAt, ownerId: userId || undefined }]);
          setBoardData({ [defaultBoard.id]: defaultBoard });
          setActiveBoardId(defaultBoard.id);
          prevBoardIdsRef.current = [defaultBoard.id];
        }
        setLoading(false);
      }
    });
    
    return () => {
      unsubscribe();
      if (boardsUnsubscribe) {
        boardsUnsubscribe();
      }
    };
  }, [realtime, boardsRefreshKey]);

  // Save board to Firestore on change (if authenticated)
  useEffect(() => {
    if (!userId || !activeBoardId) return;
    const boardId = activeBoardId;
    const board = boardData[boardId];
    if (board) {
      debouncedSaveBoard(userId, board);
    }
  }, [userId, activeBoardId, boardData]);

  // Sync card positions to Firestore every 3 minutes
  useEffect(() => {
    if (!userId || !activeBoardId) return;
    if (!workBoardMode) return;
    const boardId = activeBoardId;
    const board = boardData[boardId];
    if (!board) return;
    if (syncTimer.current) clearInterval(syncTimer.current);
    syncTimer.current = setInterval(() => {
      const currentBoard = boardData[boardId];
      if (currentBoard) {
        const updated = { ...currentBoard, extra: { ...(currentBoard.extra || {}), cardPositions: canvasPositions } };
        debouncedSaveBoard(userId, updated);
      }
    }, 180000); // 3 minutes
    return () => { if (syncTimer.current) clearInterval(syncTimer.current); };
  }, [userId, activeBoardId, workBoardMode, canvasPositions, boardData]);

  // When loading board, load positions from board.extra.cardPositions if present
  useEffect(() => {
    const board = activeBoardId ? boardData[activeBoardId] : null;
    if (board && board.extra && board.extra.cardPositions) {
      setCanvasPositions(board.extra.cardPositions);
    }
  }, [activeBoardId, boardData]);

  // Derived
  const activeBoard = activeBoardId ? boardData[activeBoardId] : null;
  const allTags = activeBoard ? Array.from(
    new Set(
      Object.values(activeBoard.cards).flatMap(card => card.tags || [])
    )
  ) : [];
  const tagCounts = activeBoard ? allTags.reduce((acc, tag) => {
    acc[tag] = Object.values(activeBoard.cards).filter(card => card.tags?.includes(tag)).length;
    return acc;
  }, {} as Record<string, number>) : {};

  // Compute user color map
  const memberIds = activeBoard?.members || (userId ? [userId] : []);
  const ownerId = activeBoard?.ownerId || userId;
  const allProfileIds = Array.from(new Set([...(memberIds || []), ownerId].filter(Boolean))) as string[];

  const [userProfileMap, setUserProfileMap] = useState<Record<string, { name: string; color: string }>>({});

  useEffect(() => {
    async function fetchProfiles() {
      if (!allProfileIds.length) return;
      const map: Record<string, { name: string; color: string }> = {};
      await Promise.all(allProfileIds.map(async (uid, idx) => {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          const data = snap.exists() ? snap.data() : {};
          map[uid] = {
            name: data.name || uid,
            color: USER_COLORS[idx % USER_COLORS.length],
          };
        } catch {
          map[uid] = { name: uid, color: USER_COLORS[idx % USER_COLORS.length] };
        }
      }));
      setUserProfileMap(map);
    }
    fetchProfiles();
  }, [activeBoardId, allProfileIds.join(',')]);

  // Add state for pending invite profiles
  const [pendingInviteProfiles, setPendingInviteProfiles] = useState<Record<string, { name: string }>>({});

  // Fetch user profiles for pending invites (by email)
  useEffect(() => {
    async function fetchInviteProfiles() {
      if (!activeBoard || !activeBoard.pendingInvites || activeBoard.pendingInvites.length === 0) {
        setPendingInviteProfiles({});
        return;
      }
      const map: Record<string, { name: string }> = {};
      await Promise.all(activeBoard.pendingInvites.map(async (email) => {
        try {
          // Query users collection by email
          const q = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const data = snap.docs[0].data();
            map[email] = { name: data.name || email };
          } else {
            map[email] = { name: email };
          }
        } catch {
          map[email] = { name: email };
        }
      }));
      setPendingInviteProfiles(map);
    }
    fetchInviteProfiles();
  }, [activeBoard?.pendingInvites?.join(',')]);

  // Board actions (update to use Firestore)
  const handleSelectBoard = (id: string) => setActiveBoardId(id);
  const handleNewBoard = () => {
    const name = prompt('Board name?') || `Board ${boards.length + 1}`;
    const newBoard = createNewBoard(name);
    setBoards([...boards, { id: newBoard.id, name, createdAt: newBoard.createdAt, updatedAt: newBoard.updatedAt, ownerId: userId || undefined }]);
    setBoardData({ ...boardData, [newBoard.id]: newBoard });
    setActiveBoardId(newBoard.id);
    if (userId) saveBoard(userId, newBoard);
  };
  const handleRenameBoard = () => {
    if (!activeBoard) return;
    const name = prompt('New board name?', activeBoard.name);
    if (!name) return;
    setBoards(boards.map(b => b.id === activeBoardId ? { ...b, name } : b));
    setBoardData({ ...boardData, [activeBoardId!]: { ...activeBoard, name } });
    if (userId) debouncedSaveBoard(userId, { ...activeBoard, name });
  };
  const handleDeleteBoard = () => {
    if (!activeBoardId || boards.length === 1) return alert('Cannot delete last board.');
    if (!window.confirm('Delete this board?')) return;
    setBoards(boards.filter(b => b.id !== activeBoardId));
    const newBoardData = { ...boardData };
    delete newBoardData[activeBoardId];
    setBoardData(newBoardData);
    const newActive = boards.find(b => b.id !== activeBoardId)?.id || null;
    setActiveBoardId(newActive);
    if (userId) deleteBoard(userId, activeBoardId);
  };

  // Kanban actions
  const handleAddColumn = () => {
    if (!activeBoard) return;
    const title = prompt('Column title?');
    if (!title) return;
    const id = `col-${Date.now()}`;
    const newCol = { id, title, cardIds: [] };
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns: [...activeBoard.columns, newCol],
      },
    });
  };
  const handleEditColumn = (colId: string, newTitle: string) => {
    if (!activeBoard) return;
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns: activeBoard.columns.map(col => col.id === colId ? { ...col, title: newTitle } : col),
      },
    });
  };
  const handleDeleteColumn = (colId: string) => {
    if (!activeBoard) return;
    if (!window.confirm('Delete this column?')) return;
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns: activeBoard.columns.filter(col => col.id !== colId),
      },
    });
  };
  const handleAddCard = (colId: string) => {
    setModalColId(colId);
    setEditCardId(null);
    setShowCardModal(true);
  };
  const handleEditCard = (colId: string, cardId: string) => {
    setModalColId(colId);
    setEditCardId(cardId);
    setShowCardModal(true);
  };
  const handleMoveCard = (result: any) => {
    if (!activeBoard) return;
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    // Remove from source
    let columns = activeBoard.columns.map(col => {
      if (col.id === source.droppableId) {
        const newCardIds = Array.from(col.cardIds);
        newCardIds.splice(source.index, 1);
        return { ...col, cardIds: newCardIds };
      }
      return col;
    });
    // Insert into destination
    columns = columns.map(col => {
      if (col.id === destination.droppableId) {
        const newCardIds = Array.from(col.cardIds);
        newCardIds.splice(destination.index, 0, draggableId);
        // Remove duplicates if any
        const seen = new Set<string>();
        const deduped: string[] = [];
        for (const id of newCardIds) {
          if (!seen.has(id)) {
            seen.add(id);
            deduped.push(id);
          }
        }
        return { ...col, cardIds: deduped };
      }
      return col;
    });
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns,
      },
    });
  };

  // Export
  const handleExport = () => {
    if (!activeBoard) return;
    let md = `# ${activeBoard.name}\n`;
    for (const col of activeBoard.columns) {
      md += `\n## ${col.title}\n`;
      for (const cardId of col.cardIds) {
        const card = activeBoard.cards[cardId];
        if (!card) continue;
        md += `\n### ${card.title}\n`;
        if (card.tags?.length) md += card.tags.map(t => `#${t}`).join(' ') + '\n';
        if (card.type === 'note' || card.type === 'resource') md += card.content + '\n';
        if (card.type === 'vuln') {
          md += `**Severity:** ${card.content.severity}\n`;
          md += `**Endpoint:** ${card.content.endpoint}\n`;
          md += `**Status:** ${card.content.status}\n`;
          md += `**Description:**\n${card.content.description}\n`;
          md += `**PoC:**\n${card.content.poc}\n`;
        }
        if (card.type === 'tool') md += `\n\  ${card.content.command}\n`;
        if (card.type === 'payload') md += `\n\  ${card.content.code}\n`;
      }
    }
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeBoard.name.replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Tag logic
  const handleToggleTag = (tag: string) => {
    setSelectedTags(selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag]);
  };
  const handleCreateTag = () => {
    const tag = prompt('New tag name?');
    // Tag creation logic can be expanded later
    if (tag && !allTags.includes(tag)) {
      // Optionally, add a dummy card with this tag to make it appear
      alert('Tag will appear when added to a card.');
    }
  };

  // Filtered cards by search/tag
  const filteredBoard = activeBoard ? {
    ...activeBoard,
    columns: activeBoard.columns.map(col => ({
      ...col,
      cardIds: col.cardIds.filter(cardId => {
        const card = activeBoard.cards[cardId];
        if (!card) return false;
        if (selectedTags.length && !selectedTags.every(tag => (card.tags || []).includes(tag))) return false;
        if (selectedTag && !(card.tags || []).includes(selectedTag)) return false;
        if (search && !card.title.toLowerCase().includes(search.toLowerCase()) && !JSON.stringify(card.content).toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    })),
  } : null;

  // Handler for attaching methodology
  const handleAttachMethodology = (methodology: Methodology | GuidedMethodology) => {
    setAttachedMethodology(methodology);
    // Add steps as cards to the To-Do column
    if (!activeBoard) return;
    const todoCol = activeBoard.columns.find(col => col.title.includes('To-Do'));
    if (!todoCol) return;
    let steps: any[] = [];
    if ('steps' in methodology && Array.isArray(methodology.steps)) {
      steps = methodology.steps.map((step: any, idx: number) => ({
        id: `meth-step-${methodology.id}-${idx}`,
        type: 'note',
        title: step.title || step.name || `Step ${idx + 1}`,
        content: step.description || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        tags: ['methodology'],
      }));
    } else if ('subMethodologies' in methodology && Array.isArray(methodology.subMethodologies)) {
      steps = methodology.subMethodologies.map((sub: any, idx: number) => ({
        id: `meth-step-${methodology.id}-${idx}`,
        type: 'note',
        title: sub.title || `Step ${idx + 1}`,
        content: sub.description || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        tags: ['methodology'],
      }));
    }
    // Add steps as cards if not already present
    const newCards = { ...activeBoard.cards };
    const newCardIds = [...todoCol.cardIds];
    steps.forEach(step => {
      if (!newCards[step.id]) {
        newCards[step.id] = step;
        newCardIds.push(step.id);
      }
    });
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        cards: newCards,
        columns: activeBoard.columns.map(col =>
          col.id === todoCol.id ? { ...col, cardIds: newCardIds } : col
        ),
      },
    });
  };

  // Handler for step check
  const handleStepCheck = (cardId: string, checked: boolean) => {
    setStepState(prev => ({ ...prev, [cardId]: { ...prev[cardId], checked } }));
  };
  // Handler for step note
  const handleStepNoteChange = (cardId: string, note: string) => {
    setStepState(prev => ({ ...prev, [cardId]: { ...prev[cardId], note } }));
  };

  // Manual refresh for one-time fetch
  const handleManualRefresh = async () => {
    if (userId) {
      setLoading(true);
      const boards = await getAllBoards(userId);
      const normalized = normalizeBoards(boards, userId);
      setBoards(normalized.map(b => ({ id: b.id, name: b.name, createdAt: b.createdAt, updatedAt: b.updatedAt, ownerId: b.ownerId || userId })));
      const data: { [id: string]: Board } = {};
      normalized.forEach(b => { data[b.id] = b; });
      setBoardData(data);
      setLoading(false);
    }
  };

  // Card modal handlers
  const handleSaveCard = (colId: string, card: CardType) => {
    if (!activeBoard || !userId) return;
    const newCard = {
      ...card,
      lastEditedBy: userId,
      lastEditedAt: Date.now(),
    };
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        cards: { ...activeBoard.cards, [card.id]: newCard },
        columns: activeBoard.columns.map(col =>
          col.id === colId && !col.cardIds.includes(card.id)
            ? { ...col, cardIds: [...col.cardIds, card.id] }
            : col
        ),
      },
    });
    setShowCardModal(false);
  };

  const handleDeleteCard = (colId: string, cardId: string) => {
    if (!activeBoard) return;
    // Remove card from cards and from column
    const { [cardId]: _, ...restCards } = activeBoard.cards;
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        cards: restCards,
        columns: activeBoard.columns.map(col =>
          col.id === colId
            ? { ...col, cardIds: col.cardIds.filter(id => id !== cardId) }
            : col
        ),
      },
    });
  };

  // Invite handler
  const processInvite = useCallback(async (rawEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'You must be signed in to invite users.' };
    }
    if (!activeBoardId) {
      return { success: false, error: 'Select a board before inviting users.' };
    }

    const email = rawEmail.trim().toLowerCase();
    if (!email) {
      return { success: false, error: 'Email address is required.' };
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return { success: false, error: 'Enter a valid email address.' };
    }

    const now = Date.now();
    if (now - lastInviteSentAt < 5000) {
      return { success: false, error: 'Please wait a few seconds before sending another invite.' };
    }

    const boardId = activeBoardId!;
    const board = boardData[boardId];
    if (!board) {
      return { success: false, error: 'Board data unavailable. Please refresh and try again.' };
    }

    const result = await sendBoardInvite(userId, email, boardId);
    if (result.success) {
      setLastInviteSentAt(now);
      setBoardData(prev => {
        const current = prev[boardId];
        if (!current) return prev;
        const pendingInvites = Array.from(new Set([...(current.pendingInvites || []), email]));
        return {
          ...prev,
          [boardId]: {
            ...current,
            pendingInvites,
          },
        };
      });
      return { success: true };
    }

    return { success: false, error: result.error || 'Failed to send invite' };
  }, [userId, activeBoardId, boardData, lastInviteSentAt]);

  const getActiveBoardSummary = useCallback(() => ({
    boardId: activeBoardId,
    boardName: activeBoard?.name || null,
    pendingInvites: activeBoard?.pendingInvites ? [...activeBoard.pendingInvites] : [],
  }), [activeBoardId, activeBoard?.name, activeBoard?.pendingInvites?.join('|')]);

  useEffect(() => {
    if (!onInviteHelpersChange) return;
    onInviteHelpersChange({
      sendInvite: processInvite,
      getActiveBoardSummary,
    });
    return () => onInviteHelpersChange(null);
  }, [onInviteHelpersChange, processInvite, getActiveBoardSummary]);

  const handleSendInvite = async () => {
    if (!inviteEmail) return;
    setInviteStatus('sending');
    setInviteError(null);
    const res = await processInvite(inviteEmail);
    if (res.success) {
      setInviteStatus('success');
      setInviteEmail('');
      setTimeout(() => setInviteStatus('idle'), 5000);
    } else {
      setInviteStatus('error');
      setInviteError(res.error || 'Failed to send invite');
    }
  };

  // Helper to get owner name
  const getOwnerName = () => {
    if (!activeBoard) return '';
    const ownerId = activeBoard.ownerId || userId || '';
    return userProfileMap[ownerId]?.name || ownerId;
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!activeBoard || !userId || !activeBoardId || memberId === userId || memberId === activeBoard.ownerId) return;
    // Remove from board's members array
    const updatedMembers = (activeBoard.members || []).filter(uid => uid !== memberId);
    setBoardData({
      ...boardData,
      [activeBoardId]: {
        ...activeBoard,
        members: updatedMembers,
      },
    });
    // Remove membership document
    await removeBoardMembership(memberId, activeBoard.id);
  };

  if (loading) return <div className="p-8 text-center">Loading Hunter's Board...</div>;
  if (!activeBoard) {
    return (
      <div className="p-8 text-center">
        No board selected.<br />
        <button
          className="btn btn-primary mt-4"
          onClick={() => {
            const name = prompt('Board name?') || 'My First Board';
            const newBoard = createNewBoard(name);
            setBoards([{ id: newBoard.id, name: newBoard.name, createdAt: newBoard.createdAt, updatedAt: newBoard.updatedAt, ownerId: userId || undefined }]);
            setBoardData({ [newBoard.id]: newBoard });
            setActiveBoardId(newBoard.id);
            if (userId) saveBoard(userId, newBoard);
          }}
        >
          + Create Your First Board
        </button>
      </div>
    );
  }
  if (!filteredBoard || !filteredBoard.columns) {
    return (
      <div className="flex-1 p-4">
        <div className="text-center text-gray-500">No board or columns to display. Please create or select a board.</div>
      </div>
    );
  }

  // Force the Attach Methodology button to render in every column for debugging
  const kanbanColumns = filteredBoard.columns.map(col => ({
    ...col,
    extraHeader: (
      <button className="btn btn-xs mb-2" onClick={() => setShowMethodologyPicker(true)}>
        Attach Methodology
      </button>
    ),
  }));

  return (
    <div className="flex">
      {showTagSidebar && (
        <TagSidebar
          tags={allTags}
          tagCounts={tagCounts}
          selectedTags={selectedTags}
          onToggleTag={handleToggleTag}
          onCreateTag={handleCreateTag}
        />
      )}
      <div className="flex-1 p-4 flex justify-center">
        <div className="w-full max-w-full max-h-full min-h-[80vh] bg-white rounded-xl shadow-md p-4 overflow-x-auto overflow-y-auto">
          <div className="flex items-center gap-4 mb-2">
            <button className="btn btn-xs" onClick={() => setShowTagSidebar(v => !v)}>
              {showTagSidebar ? 'Hide Tags' : 'Show Tags'}
            </button>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={groupByTag} onChange={e => setGroupByTag(e.target.checked)} />
              Group by Tag (coming soon)
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={workBoardMode} onChange={e => setWorkBoardMode(e.target.checked)} />
              Work Board (Canvas) Mode
            </label>
            <div className="relative">
              <button className="btn btn-xs" onClick={() => setShowInviteDropdown(v => !v)} ref={inviteBtnRef}>
                Invite User
              </button>
              {showInviteDropdown && (
                <div
                  className="absolute z-50 w-80 bg-white rounded-lg shadow-lg border p-4"
                  style={{ left: 0, bottom: '110%' }}
                >
                  <h2 className="text-lg font-bold mb-4">Invite User to Board</h2>
                  <input
                    type="email"
                    className="input input-bordered w-full mb-2"
                    placeholder="Enter user email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    disabled={inviteStatus === 'sending'}
                  />
                  <button
                    className="btn btn-primary btn-xs w-full"
                    onClick={handleSendInvite}
                    disabled={inviteStatus === 'sending' || !inviteEmail}
                  >
                    {inviteStatus === 'sending' ? 'Sending...' : 'Send Invite'}
                  </button>
                  {inviteStatus === 'success' && <div className="text-green-600 mt-2">Invite sent!</div>}
                  {inviteStatus === 'error' && <div className="text-red-600 mt-2">{inviteError}</div>}
                  <button className="btn btn-xs mt-4" onClick={() => { setShowInviteDropdown(false); setInviteStatus('idle'); setInviteEmail(''); setInviteError(null); }}>Close</button>
                </div>
              )}
            </div>
            {!realtime && (
              <button className="btn btn-xs" onClick={handleManualRefresh}>Refresh Boards</button>
            )}
          </div>
          <BoardSelector
            boards={boards}
            activeBoardId={activeBoardId!}
            onSelect={handleSelectBoard}
            onNew={handleNewBoard}
            onRename={handleRenameBoard}
            onDelete={handleDeleteBoard}
          />
          <BoardToolbar
            search={search}
            setSearch={setSearch}
            tags={allTags}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            onExport={handleExport}
            realtime={realtime}
            setRealtime={setRealtime}
          />
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-xl font-bold">{activeBoard.name}</h2>
            {(activeBoard.ownerId || userId) !== userId && (
              <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">Shared Board</span>
            )}
            <span className="ml-2 text-xs text-gray-500">Owner: {getOwnerName()}</span>
          </div>
          <div className="flex gap-4 mb-4 items-center">
            <span className="font-semibold text-sm text-gray-600">Board Members:</span>
            {Object.entries(userProfileMap).map(([uid, { name, color }]) => (
              <span key={uid} className="flex items-center gap-1">
                <span className="w-4 h-4 rounded-full inline-block" style={{ background: color }}></span>
                <span className="text-xs text-gray-700">{name}{uid === (activeBoard.ownerId || userId) ? ' (Owner)' : ''}
                  {uid !== (activeBoard.ownerId || userId) && uid !== userId && (
                    <button className="ml-1 text-red-500 underline text-xs" onClick={() => handleRemoveMember(uid)}>Remove</button>
                  )}
                </span>
              </span>
            ))}
            {/* Pending invites */}
            {activeBoard?.pendingInvites && activeBoard.pendingInvites.length > 0 && (
              <span className="ml-4 text-xs text-gray-500">Pending Invites:</span>
            )}
            {activeBoard?.pendingInvites?.map(email => (
              <span key={email} className="flex items-center gap-1">
                <span className="w-4 h-4 rounded-full inline-block bg-gray-300"></span>
                <span className="text-xs text-gray-500">{pendingInviteProfiles[email]?.name || email} ({email})</span>
              </span>
            ))}
          </div>
          {workBoardMode ? (
            <WorkBoardCanvas
              columns={filteredBoard.columns}
              cards={activeBoard.cards}
              boardId={activeBoard.id}
              userId={userId || undefined}
              userProfileMap={userProfileMap}
              onAddCard={handleAddCard}
              onDeleteCard={handleDeleteCard}
              onEditCard={handleEditCard}
              onPositionsChange={setCanvasPositions}
            />
          ) : (
            <KanbanBoard
              columns={kanbanColumns}
              cards={activeBoard.cards}
              userProfileMap={userProfileMap}
              onAddCard={handleAddCard}
              onMoveCard={handleMoveCard}
              onEditColumn={handleEditColumn}
              onDeleteColumn={handleDeleteColumn}
              onAddColumn={handleAddColumn}
              stepState={stepState}
              onStepCheck={handleStepCheck}
              onStepNoteChange={handleStepNoteChange}
              onDeleteCard={handleDeleteCard}
              onEditCard={handleEditCard}
            />
          )}
          <MethodologyPickerModal
            open={showMethodologyPicker}
            onClose={() => setShowMethodologyPicker(false)}
            onSelect={handleAttachMethodology}
          />
          <CardModal
            open={showCardModal}
            onClose={() => setShowCardModal(false)}
            onSave={handleSaveCard}
            colId={modalColId}
            card={editCardId ? activeBoard.cards[editCardId] : undefined}
            allCards={Object.values(activeBoard.cards)}
          />
        </div>
      </div>
    </div>
  );
};

export default HuntersBoard;

export type { HuntersBoardInviteHelpers };